import { mdiArrowLeft, mdiDelete } from "@mdi/js";
import Icon from "@mdi/react";
import moment from "moment";
import { FunctionComponent, useEffect, useState } from "react";
import LoadingWidget from "../../../../context_providers/modal/loader";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { fetchGet } from "../../../../service/api_client";
import FileDropZone from "../../../../ui/new_components/common/file_drop_zone";
import { FileType } from "../../../../utils/enums";
import {
  AttachmentModel,
  attachment_category,
  getFileType,
} from "../../model/purchase_order_model";
import {
  PaymentAttachmentModel,
  PaymentRequestModel,
} from "../../model/payment_request_model";

interface AttachmentSectionProps {
  attachments: AttachmentModel[];
  poNo?: PaymentRequestModel;
  onSubmit?: (data: {
    file: File;
    category: string;
    removeSelectedFile: () => void;
    setShowUpload: (v: boolean) => void;
  }) => void;
  onDelete?: (data: AttachmentModel) => void;
  onSelect: (data: AttachmentModel) => void;
}

const AttachmentBar: FunctionComponent<AttachmentSectionProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [file, setFile] = useState<File>();
  const [fileBloUrl, setFileBlobUrl] = useState<string>();
  const [fileType, setFileType] = useState<string>();
  const [category, setCategory] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [attachments, setAttachments] = useState(props.attachments);
  const [poAttachments, setPoAttachmnets] = useState<any>();

  useEffect(() => {
    if (!attachments || attachments.length === 0) {
      setShowUpload(true);
    }
  }, []);

  useEffect(() => {
    if (file) {
      const t = getFileType(file.type);
      setFileType(t);
      if ([FileType.IMAGE, FileType.PDF].includes(t)) {
        setFileBlobUrl(URL.createObjectURL(file));
      }
    }
  }, [file]);
  const removeSelectedFile = () => {
    if (fileBloUrl) {
      URL.revokeObjectURL(fileBloUrl);
      setFileBlobUrl("");
      setFile(undefined);
    }
  };

  const onSelectAttachment = async (
    attachment: PaymentAttachmentModel,
  ): Promise<void> => {
    try {
      // Fetch the file data from the URL
      const response = await fetch(attachment.url);
      if (!response.ok) {
        throw new Error("Failed to fetch the file");
      }

      // Create a blob from the response
      const blob = await response.blob();

      // Create a download link dynamically
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = attachment.url.split("/").pop() || "download.csv"; // Extract filename or set default
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the object URL
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  useEffect(() => {
    if (props.poNo?.po_no) {
      GetPOAttachments();
    }
  }, [props.poNo?.po_no]);

  const GetPOAttachments = async () => {
    setLoading(true);
    try {
      const res = await fetchGet(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/purchase-order/attachments/${props.poNo?.po_no}`,
      );
      if (res.success) {
        setPoAttachmnets(res.attachments?.attachments);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching attachments.",
      });
    } finally {
      setLoading(false); // Ensure loading state is turned off
    }
  };
  return (
    <>
      <div className="p-5 select-none">
        {showUpload ? (
          <div className="">
            {attachments && attachments.length > 0 && (
              <div className="flex justify-end">
                <button
                  id="payment_request_attachment_back_btn"
                  className="border rounded-md mb-2 text-sm px-3 py-1 flex justify-center items-center gap-1"
                  onClick={() => {
                    setShowUpload(false);
                  }}
                >
                  {" "}
                  <Icon
                    path={mdiArrowLeft}
                    size={0.8}
                    className="hover:scale-105 transform"
                  ></Icon>
                  Back
                </button>
              </div>
            )}

            <div className="flex flex-col text-sm mb-2">
              <label htmlFor="payment_request_attachment_category">Category</label>
              <select
                className="rounded border px-3 py-1  cursor-pointer focus:outline-none"
                name=""
                id="payment_request_attachment_category"
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                }}
              >
                <option value="" hidden>
                  select ...
                </option>
                {Object.values(attachment_category).map((cat) => (
                  <option value={cat} key={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {!file ? (
              <FileDropZone
                onDrop={function (files: File[]): void {
                  if (files[0]) setFile(files[0]);
                }}
              />
            ) : (
              <>
                <div className="rounded-lg border relative">
                  {fileType === FileType.IMAGE && (
                    <a target="_blank" rel="noreferrer" href={fileBloUrl}>
                      <img src={fileBloUrl} alt="" />
                    </a>
                  )}
                  {fileType === FileType.PDF && (
                    <a target="_blank" rel="noreferrer" href={fileBloUrl}>
                      <embed src={fileBloUrl} type="application/pdf"></embed>
                    </a>
                  )}

                  <button
                    id="payment_request_attachment_remove_selected_file_btn"
                    onClick={removeSelectedFile}
                    className="absolute top-0 right-0 rounded-full p-1 flex justify-center items-center "
                  >
                    <Icon path={mdiDelete} size={1}></Icon>
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-auto w-full h-auto">
              <div className="mt-5">
                <label className="text-md font-semibold my-5 py-3">
                  PR Attachments
                </label>
              </div>
              <div className="grid grid-cols-7 text-sm font-semibold px-2">
                <div className="col-span-2">File</div>
                <div className="col-span-2">category</div>
                <div className="col-span-2">created by</div>
              </div>
              <div className="flex flex-col gap-1 overflow-auto w-full h-auto">
                {attachments &&
                  attachments.length > 0 &&
                  attachments.map((att, i) => (
                    <div
                      id={`payment_request_pr_attachment_row_${att.id ?? i}`}
                      className="grid grid-cols-7 rounded-lg border overflow-hidden justify-center items-center text-xs "
                    >
                      <div
                        id={`payment_request_pr_attachment_row_${att.id ?? i}_preview_btn`}
                        onClick={() => {
                          if (att.type === "CSV") {
                            onSelectAttachment(att);
                          } else {
                            props?.onSelect(att);
                          }
                        }}
                        className="rounded-lg h-16 w-16 col-span-2 flex justify-center items-center cursor-pointer"
                      >
                        {att.type === FileType.IMAGE ? (
                          <img src={att.url} alt="Attachment preview" />
                        ) : att.type === "application/pdf" ? (
                          <div className="text-sm font-semibold">PDF</div>
                        ) : att.type === "text/csv" ||
                          att.type === "application/csv" ? (
                          <div className="text-sm font-semibold">CSV</div>
                        ) : (
                          <div className="text-sm font-semibold">
                            {att.type}
                          </div>
                        )}
                      </div>

                      <div className=" col-span-2">{att.category}</div>
                      <div className="flex flex-col   col-span-2">
                        <div className="">{att.created_by_name}</div>
                        <div className="italic " style={{ fontSize: 10 }}>
                          {moment(att.created_at).format("lll")}
                        </div>
                      </div>

                      <div className=" flex justify-center">
                        <button
                          id={`payment_request_pr_attachment_row_${att.id ?? i}_delete_btn`}
                          onClick={() => props.onDelete && props.onDelete(att)}
                          className=" rounded-full p-1 flex justify-center items-center "
                        >
                          <Icon
                            path={mdiDelete}
                            size={0.8}
                            className="hover:scale-105 transform"
                          ></Icon>
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="mt-5">
                <label className="text-md font-semibold mt-5 py-3">
                  PO Attachments
                </label>
              </div>
              <div className="grid grid-cols-4 text-sm font-semibold px-2">
                <div className="col-span-2">File</div>
                <div className="col-span-2">created by</div>
              </div>

              <div className="flex flex-col gap-1 overflow-auto w-full h-auto">
                {poAttachments && poAttachments.length > 0 ? (
                  poAttachments.map((att: any, i: any) => (
                    <div
                      id={`payment_request_po_attachment_row_${i}`}
                      key={i} // Add key for the list item for proper React rendering
                      className="grid grid-cols-4 rounded-lg border overflow-hidden justify-center items-center text-xs"
                    >
                      <div
                        id={`payment_request_po_attachment_row_${i}_preview_btn`}
                        onClick={() => {
                          if (att.type === "CSV") {
                            onSelectAttachment(att);
                          } else {
                            props?.onSelect(att);
                          }
                        }}
                        className="rounded-lg h-16 w-16 col-span-2 flex justify-center items-center cursor-pointer"
                      >
                        {att.type === FileType.IMAGE ? (
                          <img src={att.url} alt="Attachment preview" />
                        ) : att.type === "application/pdf" ? (
                          <div className="text-sm font-semibold">PDF</div>
                        ) : att.type === "text/csv" ||
                          att.type === "application/csv" ? (
                          <div className="text-sm font-semibold">CSV</div>
                        ) : (
                          <div className="text-sm font-semibold">
                            {att.type}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col col-span-2">
                        <div>{att.created_by_name}</div>
                        <div className="italic" style={{ fontSize: 10 }}>
                          {moment(att.created_at).format("lll")}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="font-bold text-green-500 text-sm">
                    No PR attachments found
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {loading && <LoadingWidget />}
    </>
  );
};

export default AttachmentBar;
